import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, AlertTriangle, IndianRupee, HeartPulse, TrendingUp, Sparkles,
  ArrowRight, ShieldCheck, ChevronRight,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { api } from "@/api";
import { formatINR, RiskBadge, initials } from "@/lib/risk";

const PULSE_COLORS = { "Healthy": "#10B981", "Watch": "#F59E0B", "High Risk": "#EF4444", "Recovered": "#0D9488" };

function KpiCard({ icon: Icon, label, value, tint, accent, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl border border-border bg-white p-5 card-shadow"
      data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center justify-between">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: tint }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </span>
      </div>
      <div className="mt-4 font-display text-3xl font-bold text-ink">{value}</div>
      <div className="mt-1 text-sm font-medium text-ink-muted">{label}</div>
    </motion.div>
  );
}

export default function Overview() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.overview().then(setData).catch(() => {}); }, []);

  if (!data) return <div className="py-24 text-center text-ink-muted">Loading retention pulse…</div>;

  const k = data.kpis;
  const pulseData = Object.entries(data.pulse).map(([name, value]) => ({ name, value }));
  const totalPulse = pulseData.reduce((a, b) => a + b.value, 0);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* Greeting */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-sm font-semibold text-indigo-brand">
          <Sparkles className="h-4 w-4" /> Retention Command Center
        </div>
        <h1 className="font-display text-3xl font-bold text-ink sm:text-4xl">Good morning, {data.owner}</h1>
        <p className="text-base text-ink-muted">Here's your student retention pulse for {data.institute}.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard i={0} icon={Users} label="Total Students" value={k.total_students} tint="#EEF0FF" accent="#4F46E5" />
        <KpiCard i={1} icon={AlertTriangle} label="Students At Risk" value={k.students_at_risk} tint="#FEF2F2" accent="#EF4444" />
        <KpiCard i={2} icon={IndianRupee} label="Revenue At Risk" value={formatINR(k.revenue_at_risk)} tint="#FFFBEB" accent="#D97706" />
        <KpiCard i={3} icon={HeartPulse} label="Students Recovered" value={k.students_recovered} tint="#ECFDF5" accent="#059669" />
        <KpiCard i={4} icon={TrendingUp} label="Retention Rate" value={`${k.retention_rate}%`} tint="#EEF0FF" accent="#4F46E5" />
      </div>

      {/* Insight + Pulse + Revenue protected */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* AI Insight */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="relative overflow-hidden rounded-2xl bg-ink p-6 text-white card-shadow-lg lg:col-span-2"
          data-testid="insight-card"
        >
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-indigo-brand/30 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 text-sm font-semibold text-indigo-200">
              <Sparkles className="h-4 w-4" /> BatchPulse Insight
            </div>
            <p className="mt-4 max-w-2xl font-display text-xl font-semibold leading-relaxed sm:text-2xl">
              {data.insight}
            </p>
            <p className="mt-3 text-sm text-white/60">Based on current engagement data · updated today</p>
            <Button
              onClick={() => nav("/risk-queue")}
              data-testid="review-risk-queue-btn"
              className="mt-5 gap-2 rounded-xl bg-indigo-brand font-semibold hover:bg-white hover:text-ink"
            >
              Review Risk Queue <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </motion.div>

        {/* Retention Pulse */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}
          className="rounded-2xl border border-border bg-white p-6 card-shadow"
          data-testid="retention-pulse"
        >
          <h3 className="font-display text-lg font-bold text-ink">Retention Pulse</h3>
          <div className="relative mx-auto mt-2 h-44 w-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pulseData} dataKey="value" innerRadius={58} outerRadius={82} paddingAngle={3} stroke="none" isAnimationActive={false}>
                  {pulseData.map((e) => <Cell key={e.name} fill={PULSE_COLORS[e.name]} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-display text-3xl font-bold text-ink">{totalPulse}</span>
              <span className="text-xs text-ink-muted">students</span>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {pulseData.map((e) => (
              <div key={e.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 font-medium text-ink-soft">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: PULSE_COLORS[e.name] }} />
                  {e.name}
                </span>
                <span className="font-bold text-ink">{e.value}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Revenue protected banner */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 sm:flex-row sm:items-center"
        data-testid="revenue-protected"
      >
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500 text-white">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <div>
            <div className="text-sm font-semibold text-emerald-700">Revenue Protected This Month</div>
            <div className="font-display text-3xl font-bold text-emerald-800">{formatINR(k.revenue_protected)}</div>
          </div>
        </div>
        <p className="max-w-md text-sm text-emerald-700/80">
          Estimated recurring monthly revenue retained after successful interventions.
        </p>
      </motion.div>

      {/* Needs attention now */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-ink">Needs Attention Now</h2>
          <Button variant="ghost" onClick={() => nav("/risk-queue")} className="gap-1 text-indigo-brand hover:text-indigo-deep">
            View all <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {data.urgent.map((s, i) => (
            <motion.button
              key={s.id}
              onClick={() => nav(`/students/${s.id}`)}
              data-testid={`urgent-card-${s.id}`}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.24 + i * 0.06 }}
              whileHover={{ y: -4 }}
              className="group rounded-2xl border border-border bg-white p-5 text-left card-shadow transition-shadow hover:card-shadow-lg"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ink font-display text-sm font-bold text-white">
                    {initials(s.name)}
                  </span>
                  <div>
                    <div className="font-bold text-ink">{s.name}</div>
                    <div className="text-xs text-ink-muted">{s.grade} · {s.course}</div>
                  </div>
                </div>
                <span className="font-display text-2xl font-bold text-risk-high">{s.risk_score}</span>
              </div>
              <div className="mt-3"><RiskBadge level={s.risk_level} /></div>
              <ul className="mt-4 space-y-1.5">
                {s.risk_breakdown.filter((b) => b.points > 0).slice(0, 4).map((b) => (
                  <li key={b.label} className="flex items-start gap-2 text-xs text-ink-soft">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-ink-muted" />
                    <span className="flex-1">{b.detail}</span>
                    <span className="font-bold text-ink-muted">+{b.points}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                <span className="text-sm font-semibold text-ink">{formatINR(s.monthly_fee)}<span className="text-xs font-normal text-ink-muted">/month</span></span>
                <span className="flex items-center gap-1 text-sm font-semibold text-indigo-brand group-hover:gap-2 transition-all">
                  View &amp; Rescue <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}
